import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const defaultMessage = "Você tem alterações não salvas. Tem certeza de que deseja sair?";

const getRoutes = () => {
  try {
    return JSON.parse(sessionStorage.getItem("rotas")) || [];
  } catch {
    return [];
  }
};

const parseFunnelMappingRoute = (route) => {
  const match = route?.match(/\/funil-de-obras\/([^/]+)\/(mapeamento|acompanhamento-mapeamento)$/);

  if (!match) return null;

  return {
    clienteId: match[1],
    step: match[2],
  };
};

const shouldSkipPreviousRoute = (previousRoute, currentPath) => {
  const previousFunnelRoute = parseFunnelMappingRoute(previousRoute);
  const currentFunnelRoute = parseFunnelMappingRoute(currentPath);

  return (
    previousFunnelRoute?.clienteId === currentFunnelRoute?.clienteId &&
    previousFunnelRoute?.step === "mapeamento" &&
    currentFunnelRoute?.step === "acompanhamento-mapeamento"
  );
};

const getPreviousRoute = (currentPath) => {
  const routes = getRoutes();

  for (let index = routes.length - 1; index >= 0; index -= 1) {
    const previousRoute = routes[index];

    if (previousRoute === currentPath) continue;
    if (shouldSkipPreviousRoute(previousRoute, currentPath)) continue;

    return previousRoute;
  }

  return null;
};

const useFunnelUnsavedChanges = (isDirty, message = defaultMessage, onExit) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!isDirty) return undefined;

    let confirmedExit = false;
    window.history.pushState(null, "", window.location.href);

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    const handlePageHide = () => {
      onExit?.();
    };

    const handlePopState = () => {
      if (confirmedExit) return;
      if (window.confirm(message)) {
        confirmedExit = true;
        window.removeEventListener("popstate", handlePopState);
        Promise.resolve(onExit?.()).finally(() => {
          const previousRoute = getPreviousRoute(window.location.pathname);
          if (previousRoute) {
            navigate(previousRoute);
          } else {
            window.history.go(-1);
          }
        });
      } else {
        window.history.pushState(null, "", window.location.href);
      }
    };

    const handleClickCapture = (event) => {
      const target = event.target.closest("a, .back-button, .confirm-unsaved");
      if (!target) return;
      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      confirmedExit = true;
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleClickCapture, true);

      const anchor = target.tagName === "A" ? target : null;
      const href = anchor?.getAttribute("href");

      const doNavigate = () => {
        if (anchor && href) {
          try {
            const url = new URL(anchor.href, window.location.origin);
            if (url.origin === window.location.origin) {
              navigate(`${url.pathname}${url.search}${url.hash}`);
            } else {
              window.location.href = anchor.href;
            }
          } catch {
            navigate(href);
          }
          return;
        }

        const freshPrevious = getPreviousRoute(window.location.pathname);
        if (freshPrevious) {
          navigate(freshPrevious);
        } else {
          window.history.go(-1);
        }
      };

      Promise.resolve(onExit?.()).finally(doNavigate);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("click", handleClickCapture, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleClickCapture, true);
    };
  }, [isDirty, message, navigate, onExit, pathname]);
};

export default useFunnelUnsavedChanges;
